import {isLoginUserServer} from '@financial/common-utils'
import isNil from '@naverpay/hidash/isNil'
import {queryOptions} from '@tanstack/react-query'

import {createApi} from '$share/utils/api'
import {internalInstance, LOGIN_REQUIRED_API_PATH} from '$utils/api'

import type {
    PossibleRequest,
    QueryArgs,
    QueryParams,
    ServerQueryArgs,
    TanstackQueryData,
    UseMutationData,
} from '$share/types/query'
import type {MutuallyExclusive} from '$types/utility-types'
import type {AxiosError} from 'axios'

const getQueryKey = <Request extends PossibleRequest = undefined>({
    fetcher,
    url,
    method,
    params,
    customKey,
}: Pick<QueryParams, 'fetcher' | 'method' | 'url' | 'customKey'> & {
    params?: Request
}) => {
    const apiRoute = `${fetcher?.defaults.baseURL}${url}`

    const [...queryKeys] = new URL(apiRoute).pathname.split('/').filter((v) => v)

    const paramKeys = Object.entries(params ?? {})
        .filter(([k, v]) => !!k && !isNil(v))
        .flat()

    const customKeys = customKey ? [customKey] : []

    return [method, ...queryKeys, ...paramKeys, ...customKeys]
}

const createQueryOptions = <Response, Request extends PossibleRequest = undefined>({
    args,
    getConfig,
}: {
    args?: MutuallyExclusive<QueryArgs<Request>, ServerQueryArgs<Request>>
    getConfig: (request?: Request) => QueryParams
}) => {
    const {params, data, customKey} = args ?? {}

    const {url, method, fetcher = internalInstance} = getConfig(params)

    const key = getQueryKey<Request>({
        url,
        method,
        params,
        fetcher,
        customKey,
    })

    return queryOptions<Response, AxiosError>({
        queryKey: key,
        queryFn: async () => {
            const api = createApi(fetcher)
            const {data: response} = await api<Response>(url, {
                data,
                method,
                params,
                ...(args?.req ? {req: args.req} : {}),
            })
            return response
        },
    })
}

export function createTanstackQuery<Response, Request extends PossibleRequest = undefined>(
    getConfig: (request?: Request) => QueryParams,
): TanstackQueryData<Response, Request> {
    return {
        query: (args) => {
            return createQueryOptions<Response, Request>({
                args,
                getConfig,
            })
        },
        serverQuery: (args) => {
            const {fetcher = internalInstance} = getConfig()
            const baseURL = fetcher.defaults.baseURL

            if (
                LOGIN_REQUIRED_API_PATH.some((pathname) => baseURL?.endsWith(pathname)) &&
                args?.req &&
                !isLoginUserServer(args.req)
            ) {
                return null
            }

            return {
                type: 'query',
                queryOptions: createQueryOptions<Response, Request>({
                    args,
                    getConfig,
                }),
            }
        },
        mutation: (() => {
            const {fetcher = internalInstance, ...config} = getConfig()

            return {
                mutationKey: getQueryKey<Request>({fetcher, ...config}),
                mutationFn: async (request?: Request) => {
                    const api = createApi(fetcher)
                    const {data: response} = await api<Response>(config.url, {
                        ...config,
                        ...(['GET', 'get', 'delete', 'DELETE'].includes(config.method)
                            ? {
                                  params: request,
                              }
                            : {
                                  data: request,
                              }),
                    })
                    return response
                },
            }
        }) satisfies UseMutationData<Response, Request>,
    }
}
